import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eventApi, paymentApi } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';
import { Star, Check, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FeatureMyEvent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isCommunityMember, isBusinessOwner, loading: authLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricing(r.data)).catch(() => setPricing(null));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || (!isCommunityMember && !isBusinessOwner)) {
      navigate('/community-login');
      return;
    }
    loadEvents();
    
    // Check for cancelled payment
    if (searchParams.get('cancelled') === 'true') {
      toast.info('Payment was cancelled');
    }
  }, [isAuthenticated, isCommunityMember, isBusinessOwner, authLoading, navigate, searchParams]);

  const loadEvents = async () => {
    try {
      const response = await eventApi.getMine();
      const approvedEvents = response.data.filter(e => e.status === 'approved' && !e.is_featured);
      setEvents(approvedEvents);
      
      // Pre-select event from URL
      const eventId = searchParams.get('event');
      if (eventId) {
        const event = approvedEvents.find(e => e.id === eventId);
        if (event) setSelectedEvent(event);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (productType) => {
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }
    
    setProcessingPayment(true);
    try {
      const response = await paymentApi.createEventCheckout(productType, selectedEvent.id);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12" data-testid="feature-event-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-accent" />
          </div>
          <h1 className="text-4xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Feature Your Event
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Get premium visibility and attract more attendees to your event
          </p>
        </div>

        {/* Select Event */}
        {events.length > 0 ? (
          <div className="bg-white rounded-2xl p-6 border border-neutral-200 mb-12 max-w-xl mx-auto">
            <h3 className="font-bold text-lg mb-4">Select Event to Feature</h3>
            <select
              value={selectedEvent?.id || ''}
              onChange={(e) => setSelectedEvent(events.find(ev => ev.id === e.target.value))}
              className="form-input"
              data-testid="event-select"
            >
              <option value="">Choose an event</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.event_name} - {event.event_date}
                </option>
              ))}
            </select>
            
            {selectedEvent && (
              <div className="mt-4 p-4 bg-neutral-50 rounded-xl">
                <h4 className="font-semibold">{selectedEvent.event_name}</h4>
                <p className="text-sm text-neutral-600">{selectedEvent.event_date} • {selectedEvent.category}</p>
                <p className="text-sm text-neutral-500">{selectedEvent.location}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-8 border border-neutral-200 mb-12 max-w-xl mx-auto text-center">
            <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-bold text-lg mb-2">No Approved Events</h3>
            <p className="text-neutral-600 mb-4">
              You need an approved event to purchase featured placement.
            </p>
            <button
              onClick={() => navigate('/submit-event')}
              className="btn-primary"
            >
              Submit an Event
            </button>
          </div>
        )}

        {/* Pricing Cards */}
        {events.length > 0 && (
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Weekly Plan */}
            <div className="pricing-card">
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Weekly Featured</h3>
              <p className="text-neutral-600 mb-6">7 days of premium visibility</p>
              
              <div className="pricing-amount mb-6">
                ${pricing?.event_weekly?.amount ?? 10}
                {pricing?.event_weekly?.promo_active && pricing?.event_weekly?.base_amount !== pricing?.event_weekly?.amount && (
                  <span className="text-lg text-neutral-400 line-through ml-2">${pricing?.event_weekly?.base_amount}</span>
                )}
                <span> / week</span>
              </div>

              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>Homepage Featured Events section</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>Featured badge on listing</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>Priority in calendar view</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>7 days of visibility</span>
                </li>
              </ul>

              <button
                onClick={() => handlePurchase('event_weekly')}
                disabled={processingPayment || !selectedEvent}
                className="btn-secondary w-full"
                data-testid="purchase-weekly-btn"
              >
                {processingPayment ? 'Processing...' : 'Get Started'}
              </button>
            </div>

            {/* Until Event Date Plan */}
            <div className="pricing-card popular">
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Until Event Date</h3>
              <p className="text-neutral-600 mb-6">Featured until your event happens</p>
              
              <div className="pricing-amount mb-6">
                ${pricing?.event_until_date?.amount ?? 25}
                {pricing?.event_until_date?.promo_active && pricing?.event_until_date?.base_amount !== pricing?.event_until_date?.amount && (
                  <span className="text-lg text-neutral-400 line-through ml-2">${pricing?.event_until_date?.base_amount}</span>
                )}
                <span> one-time</span>
              </div>

              <ul className="space-y-3 text-left mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>Homepage Featured Events section</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>Featured badge on listing</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>Priority in calendar view</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-accent">Featured until event date</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary" />
                  <span>Auto-expires after event</span>
                </li>
              </ul>

              <button
                onClick={() => handlePurchase('event_until_date')}
                disabled={processingPayment || !selectedEvent}
                className="btn-accent w-full"
                data-testid="purchase-until-date-btn"
              >
                {processingPayment ? 'Processing...' : 'Get Featured'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureMyEvent;
